import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Receipt, Search, Calendar, Loader2, Download, Printer, Eye,
  ChevronLeft, ChevronRight, Filter, XCircle, AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { SellerDashboardLayout } from "./Dashboard";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const SalesHistoryPage = () => {
  const { t, i18n } = useTranslation("seller");
  const { t: tn } = useTranslation("nav");
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const isOwner = user?.role === "pharmacy";

  useEffect(() => {
    fetchSales();
  }, []);

  useEffect(() => {
    // Check if we should open a specific sale
    const saleId = searchParams.get('sale');
    if (saleId && sales.length > 0) {
      const sale = sales.find(s => s.id === saleId);
      if (sale) {
        setSelectedSale(sale);
        setShowDetailDialog(true);
      }
    }
  }, [searchParams, sales]);

  useEffect(() => {
    filterSales();
  }, [searchQuery, dateFilter, customDate, sales]);

  const fetchSales = async () => {
    try {
      const res = await apiClient.get("/pos/sales?period=all");
      // Handle both direct array or {sales: []} response
      const salesData = Array.isArray(res.data) ? res.data : (res.data.sales || []);
      setSales(salesData);
      setFilteredSales(salesData);
    } catch (error) {
      console.error("Error fetching sales:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const filterSales = () => {
    let filtered = [...sales];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sale =>
        sale.invoice_number?.toLowerCase().includes(query) ||
        sale.client_name?.toLowerCase().includes(query) ||
        sale.client_phone?.includes(query)
      );
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.created_at);
        switch (dateFilter) {
          case "today":
            return saleDate.toDateString() === now.toDateString();
          case "yesterday":
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            return saleDate.toDateString() === yesterday.toDateString();
          case "week":
            const weekAgo = new Date(now);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return saleDate >= weekAgo;
          case "month":
            return saleDate.getMonth() === now.getMonth() && 
                   saleDate.getFullYear() === now.getFullYear();
          case "custom":
            if (customDate) {
              const target = new Date(customDate);
              return saleDate.toDateString() === target.toDateString();
            }
            return true;
          default:
            return true;
        }
      });
    }

    setFilteredSales(filtered);
  };

  const handleRequestCancellation = async () => {
    if (!selectedSale || !cancelReason.trim()) {
      toast.error(t("sales.toastEnterReason"));
      return;
    }
    
    setCancelLoading(true);
    try {
      await apiClient.post(`/pos/sales/${selectedSale.id}/request-cancellation`, {
        reason: cancelReason
      });
      toast.success(t("sales.toastRequestSent"));
      setShowCancelDialog(false);
      setCancelReason("");
      fetchSales(); // Refresh
    } catch (error) {
      console.error("Error requesting cancellation:", error);
      toastApiError(error);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleValidateCancellation = async (approved) => {
    if (!selectedSale) return;
    
    setCancelLoading(true);
    try {
      await apiClient.post(`/pos/sales/${selectedSale.id}/validate-cancellation`, {
        approved,
        note: approved ? t("sales.noteApproved") : t("sales.noteRejected")
      });
      toast.success(approved ? t("sales.toastCancelled") : t("sales.toastRejected"));
      setShowDetailDialog(false);
      fetchSales(); // Refresh
    } catch (error) {
      console.error("Error validating cancellation:", error);
      toastApiError(error);
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusBadge = (sale) => {
    switch (sale.status) {
      case "cancelled":
        return <Badge className="bg-red-100 text-red-700">{t("sales.statusCancelled")}</Badge>;
      case "cancellation_requested":
        return <Badge className="bg-amber-100 text-amber-700">{t("sales.statusCancellationRequested")}</Badge>;
      default:
        return <Badge className="bg-green-100 text-green-700">{t("sales.statusCompleted")}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: 'CDF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setShowDetailDialog(true);
  };

  const handlePrintReceipt = (saleId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pos/invoice/${saleId}/pdf`, '_blank');
  };

  const calculateTotalRevenue = () => {
    return filteredSales.reduce((sum, sale) => sum + (sale.total || 0), 0);
  };

  if (loading) {
    return (
      <SellerDashboardLayout title={tn("seller.salesHistory")}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
        </div>
      </SellerDashboardLayout>
    );
  }

  return (
    <SellerDashboardLayout title={tn("seller.salesHistory")}>
      {/* Summary Card */}
      <Card className="border-0 shadow-soft mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500">{t("sales.totalDisplayed")}</p>
              <p className="text-3xl font-bold text-omu-blue-600">
                {formatCurrency(calculateTotalRevenue())}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {t("sales.salesCount", { count: filteredSales.length })}
              </p>
            </div>
            <Button 
              className="bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
              onClick={() => navigate("/seller/pos")}
            >
              {t("dashboard.newSale")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-0 shadow-soft mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("sales.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-sales"
              />
            </div>
            <Select value={dateFilter} onValueChange={(v) => { setDateFilter(v); if (v !== "custom") setCustomDate(""); }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t("sales.filterPeriod")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("sales.filterAllDates")}</SelectItem>
                <SelectItem value="today">{t("sales.filterToday")}</SelectItem>
                <SelectItem value="yesterday">{t("sales.filterYesterday")}</SelectItem>
                <SelectItem value="week">{t("sales.filterWeek")}</SelectItem>
                <SelectItem value="month">{t("sales.filterMonth")}</SelectItem>
                <SelectItem value="custom">{t("sales.filterCustom")}</SelectItem>
              </SelectContent>
            </Select>
            {dateFilter === "custom" && (
              <Input
                type="date"
                className="w-full sm:w-[180px] rounded-xl"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                data-testid="custom-date-input"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card className="border-0 shadow-soft">
        <CardContent className="p-0">
          {filteredSales.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t("sales.noSales")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("sales.colInvoice")}</TableHead>
                    <TableHead>{t("sales.colDate")}</TableHead>
                    <TableHead>{t("sales.colClient")}</TableHead>
                    <TableHead>{t("sales.items")}</TableHead>
                    <TableHead>{t("sales.colStatus")}</TableHead>
                    <TableHead className="text-right">{t("sales.total")}</TableHead>
                    <TableHead className="text-right">{t("sales.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => (
                    <TableRow key={sale.id} className={sale.status === "cancelled" ? "opacity-60" : ""}>
                      <TableCell className="font-medium">
                        {sale.invoice_number}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(sale.created_at)}
                      </TableCell>
                      <TableCell>
                        {sale.client_name || (
                          <span className="text-slate-400">{t("sales.anonymous")}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {t("sales.itemsCount", { count: sale.items?.length || 0 })}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(sale)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-omu-blue-600">
                        {formatCurrency(sale.total)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewSale(sale)}
                            title={t("sales.viewDetails")}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePrintReceipt(sale.id)}
                            title={t("actions.print")}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          {sale.status === "completed" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => {
                                setSelectedSale(sale);
                                setShowCancelDialog(true);
                              }}
                              title={t("sales.requestCancellation")}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sale Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-omu-blue-500" />
              {t("sales.saleDetail")}
            </DialogTitle>
          </DialogHeader>

          {selectedSale && (
            <div className="space-y-4">
              {/* Invoice Info */}
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">
                  {selectedSale.invoice_number}
                </p>
                <p className="text-sm text-slate-500">
                  {formatDate(selectedSale.created_at)}
                </p>
              </div>

              {/* Client Info */}
              {selectedSale.client_name && (
                <div className="p-3 border rounded-lg">
                  <p className="text-sm text-slate-500">{t("sales.client")}</p>
                  <p className="font-medium">{selectedSale.client_name}</p>
                  {selectedSale.client_phone && (
                    <p className="text-sm text-slate-600">{selectedSale.client_phone}</p>
                  )}
                </div>
              )}

              {/* Items */}
              <div className="border rounded-lg divide-y">
                {selectedSale.items?.map((item, i) => (
                  <div key={i} className="p-3 flex justify-between">
                    <div>
                      <p className="font-medium">{item.medicine_name}</p>
                      <p className="text-sm text-slate-500">
                        {formatCurrency(item.unit_price)} x {item.quantity}
                      </p>
                    </div>
                    <p className="font-semibold">
                      {formatCurrency(item.unit_price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex justify-between items-center p-4 bg-omu-blue-50 rounded-lg">
                <span className="text-lg font-semibold">{t("sales.total")}</span>
                <span className="text-2xl font-bold text-omu-blue-600">
                  {formatCurrency(selectedSale.total)}
                </span>
              </div>

              {/* Cancellation Request Info */}
              {selectedSale.status === "cancellation_requested" && selectedSale.cancellation_request && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-800">{t("sales.pendingCancellation")}</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        <strong>{t("sales.requestedBy")}</strong> {selectedSale.cancellation_request.requested_by_name}
                      </p>
                      <p className="text-sm text-amber-700">
                        <strong>{t("sales.reasonLabel")}</strong> {selectedSale.cancellation_request.reason}
                      </p>
                      {isOwner && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleValidateCancellation(false)}
                            disabled={cancelLoading}
                          >
                            {t("sales.refuse")}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => handleValidateCancellation(true)}
                            disabled={cancelLoading}
                          >
                            {t("sales.approveCancellation")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedSale.status === "cancelled" && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="h-5 w-5" />
                    <span className="font-semibold">{t("sales.saleCancelledMsg")}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDetailDialog(false)}
                >
                  {t("actions.close")}
                </Button>
                <Button
                  className="flex-1 bg-omu-blue-500 hover:bg-omu-blue-600"
                  onClick={() => handlePrintReceipt(selectedSale.id)}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  {t("actions.print")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancellation Request Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("sales.requestCancelTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                {t("sales.cancelDialogDesc")}
              </p>
            </div>
            
            <div>
              <Label htmlFor="cancel-reason">{t("sales.cancellationReason")} *</Label>
              <Textarea
                id="cancel-reason"
                placeholder={t("sales.cancellationExamplePlaceholder")}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleRequestCancellation}
              disabled={cancelLoading || !cancelReason.trim()}
            >
              {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("sales.submitRequest")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerDashboardLayout>
  );
};

export default SalesHistoryPage;
